import React, { useState, useEffect, useRef } from "react";
import { Table } from "react-bootstrap";
import { Button } from "react-bootstrap";
import QRScanner from "utils/QRScanner";
import { toast } from "react-toastify";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import ActionButton from "components/Buttons/ActionButton";
import StockTransferBagsModal from "./StockTransferBagsModal";
import { DeleteButton } from "components/Buttons/DeleteButton";

interface DispatchItem {
  dispatch_item_id: string;
  master_item_id: string;
  item_name?: string;
  item_code?: string;
  category?: any;
  sub_category?: any;
  unit?: any;
  total_quantity: number;
  dispatch_quantity: number;
  bags?: Array<{
    bag_no: string;
    status?: "verified" | "not_verified" | null;
    inventory_item_id: string;
    total_quantity: number;
    issued_quantity: number;
  }>;
  remarks?: string;
}

interface ScannedItem {
  dispatch_item_id: string;
  item_id: string;
  inventory_item_id: string;
  bag_no: string;
  received_quantity: number;
  damaged_quantity?: number;
  selling_price?: number;
  remark?: string;
}

interface StockTransferInwardTableProps {
  dispatchItems: DispatchItem[];
  onItemsScanned: (items: ScannedItem[]) => void;
  initialScannedItems?: ScannedItem[];
  onDispatchItemsUpdate?: (updatedItems: DispatchItem[]) => void;
  isEditMode?: boolean;
}

const StockTransferInwardTable: React.FC<StockTransferInwardTableProps> = ({
  dispatchItems,
  onItemsScanned,
  initialScannedItems = [],
  onDispatchItemsUpdate,
  isEditMode = false,
}) => {
  const [showScanner, setShowScanner] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DispatchItem | null>(null);
  const [scannedItems, setScannedItems] = useState<Map<string, ScannedItem[]>>(
    new Map()
  );

  // states for ViewBagsModal
  const [show, setShow] = useState<boolean>(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null);
  const [viewBagsForItem, setViewBagsForItem] = useState<any[]>([]);
  const [verifiedItems, setVerifiedItems] = useState<Record<number, number[]>>(
    {}
  );
  const [dispatch_items, set_dispatch_items] = useState<any[]>([]);
  const initializedRef = useRef(false);

  // Initialize local dispatch_items state from prop when it changes
  useEffect(() => {
    if (dispatchItems && dispatchItems.length > 0) {
      // clone to avoid mutating props
      set_dispatch_items(dispatchItems.map((d) => ({ ...d })));
    } else {
      set_dispatch_items([]);
    }
    // Reset initialization flag when dispatchItems change
    initializedRef.current = false;
  }, [dispatchItems]);

  // Initialize scanned items and verified items from props (for edit mode)
  // Only initialize once when data is first loaded
  useEffect(() => {
    if (initializedRef.current) return;

    // Initialize scanned items from initialScannedItems prop
    if (initialScannedItems && initialScannedItems.length > 0) {
      const scannedMap = new Map<string, ScannedItem[]>();
      initialScannedItems.forEach((item) => {
        const existing = scannedMap.get(item.dispatch_item_id) || [];
        scannedMap.set(item.dispatch_item_id, [...existing, item]);
      });
      setScannedItems(scannedMap);
    }

    // Initialize verifiedItems based on dispatch_items bags status
    if (dispatch_items && dispatch_items.length > 0) {
      const verified: Record<number, number[]> = {};
      dispatch_items.forEach((item: any, idx: number) => {
        if (item.bags && Array.isArray(item.bags)) {
          const verifiedBagIndices: number[] = [];
          item.bags.forEach((bag: any, bagIdx: number) => {
            if (bag.status === "verified") {
              verifiedBagIndices.push(bagIdx);
            }
          });
          if (verifiedBagIndices.length > 0) {
            verified[idx] = verifiedBagIndices;
          }
        }
      });
      if (Object.keys(verified).length > 0) {
        setVerifiedItems(verified);
      }
    }

    // Mark as initialized if we have the necessary data
    if ((initialScannedItems && initialScannedItems.length > 0) || 
        (dispatch_items && dispatch_items.length > 0)) {
      initializedRef.current = true;
    }
  }, [initialScannedItems, dispatch_items]);

  const handleRemarkChange = (index: number, value: string) => {
    set_dispatch_items((prev) => {
      const updated = prev.map((it, i) => (i === index ? { ...it, remarks: value } : it));
      return updated;
    });
  };

  const handleScanClick = (item: DispatchItem) => {
    setSelectedItem(item);
    // setShowScanner(true);
  };

  const handleScanResult = (scannedValue: string) => {
    if (!selectedItem) return;

    try {
      let inventoryItemId: string;
      let bagNo: string = "";

      try {
        const parsed = JSON.parse(scannedValue);
        inventoryItemId = parsed.inventory_item_id || parsed.id || scannedValue;
        bagNo = parsed.bag_no || "";
      } catch {
        inventoryItemId = scannedValue;
      }

      const bag = selectedItem.bags?.find(
        (b) => b.inventory_item_id === inventoryItemId
      );

      if (!bag) {
        toast.error(`Inventory item ${inventoryItemId} not found`);
        return;
      }

      const alreadyScanned = Array.from(scannedItems.values())
        .flat()
        .some((s) => s.inventory_item_id === inventoryItemId);

      if (alreadyScanned) {
        toast.warning("This item has already been scanned");
        return;
      }

      const scannedItem: ScannedItem = {
        dispatch_item_id: selectedItem.dispatch_item_id,
        item_id: selectedItem.master_item_id,
        inventory_item_id: inventoryItemId,
        bag_no: bag.bag_no || bagNo,
        received_quantity: bag.issued_quantity || bag.total_quantity,
        damaged_quantity: 0,
        selling_price: undefined,
        remark: "",
      };

      const newMap = new Map(scannedItems);
      const existingForItem = newMap.get(selectedItem.dispatch_item_id) || [];
      const updatedScans = [...existingForItem, scannedItem];
      newMap.set(selectedItem.dispatch_item_id, updatedScans);

      setScannedItems(newMap);

      const allScannedItems = Array.from(newMap.values()).flat();
      onItemsScanned(allScannedItems);

      toast.success(`Item scanned: ${bag.bag_no || inventoryItemId}`);
    } catch (error) {
      console.error("Error processing scan:", error);
      toast.error("Failed to process scanned item");
    } finally {
      setShowScanner(false);
      setSelectedItem(null);
    }
  };

  const handleRemoveScannedItem = (
    dispatchItemId: string,
    inventoryItemId: string
  ) => {
    const newMap = new Map(scannedItems);
    const existingScans = newMap.get(dispatchItemId) || [];

    const updated = existingScans.filter(
      (s) => s.inventory_item_id !== inventoryItemId
    );

    updated.length === 0
      ? newMap.delete(dispatchItemId)
      : newMap.set(dispatchItemId, updated);

    setScannedItems(newMap);

    // Update bag statuses when item is removed
    set_dispatch_items((prevItems) => {
      const updated = prevItems.map((item: any) => {
        if (item.dispatch_item_id === dispatchItemId) {
          // Update bag statuses - mark as not_verified if removed from scanned items
          const updatedBags = (item.bags || []).map((bag: any) => {
            if (bag.inventory_item_id === inventoryItemId) {
              return {
                ...bag,
                status: "not_verified",
              };
            }
            return bag;
          });
          return {
            ...item,
            bags: updatedBags,
          };
        }
        return item;
      });
      
      // Notify parent component of the update
      if (onDispatchItemsUpdate) {
        onDispatchItemsUpdate(updated);
      }
      
      return updated;
    });

    const allScannedItems = Array.from(newMap.values()).flat();
    onItemsScanned(allScannedItems);

    toast.success("Item removed");
  };

  const getScannedCount = (dispatchItemId: string) =>
    scannedItems.get(dispatchItemId)?.length || 0;

  console.log("dispatch_items : ",dispatch_items)
  return (
    <>
      <QRScanner
        show={showScanner}
        handleClose={() => {
          setShowScanner(false);
          setSelectedItem(null);
        }}
        onResult={handleScanResult}
      />

      <div className="mb-4 mt-3">
        <Table hover responsive className="align-middle text-center">
          <thead>
            <tr>
              <th style={{ ...tableStyles.headerAlt }}>Sr.No</th>
              <th style={{ ...tableStyles.headerAlt }}>Item Name</th>
              <th style={{ ...tableStyles.headerAlt }}>Item Code</th>
              <th style={{ ...tableStyles.headerAlt }}>Category</th>
              <th style={{ ...tableStyles.headerAlt }}>Sub Category</th>
              <th style={{ ...tableStyles.headerAlt }}>Unit</th>
              <th style={{ ...tableStyles.headerAlt }}>Issued Quantity</th>
              {!isEditMode && <th style={{ ...tableStyles.headerAlt }}>Scan Status</th>}
              <th style={{ ...tableStyles.headerAlt }}>Remarks</th>

              {!isEditMode && (
                <th
                  style={{
                    ...tableStyles.headerAlt,
                    ...tableStyles.actionHeader,
                  }}
                >
                  Action
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {dispatch_items.length === 0 ? (
              <tr>
                <td colSpan={isEditMode ? 7 : 9} className="text-center py-4">
                  No items found in this dispatch
                </td>
              </tr>
            ) : (
              dispatch_items.map((item, idx) => {
                return (
                  <tr key={item.dispatch_item_id} style={tableStyles.rowHover}>
                    <td style={tableStyles.cell}>{idx + 1}</td>
                    <td style={tableStyles.cell}>{item.item_name || "-"}</td>
                    <td style={tableStyles.cell}>{item.item_code || "-"}</td>
                    <td style={tableStyles.cell}>
                      {item?.category?.category_name || "-"}
                    </td>
                    <td style={tableStyles.cell}>
                      {item?.sub_category?.sub_category_name || "-"}
                    </td>
                    <td style={tableStyles.cell}>
                      {item?.unit?.unit_name || "-"}
                    </td>
                    <td style={tableStyles.cell}>{item.total_quantity}</td>

                    {!isEditMode && (
                      <td style={tableStyles.cell}>
                        {item?.bags && item.bags.length > 0 ? (
                          (() => {
                            const totalBags = item.bags?.length || 0;
                            const verifiedBags = item.bags?.filter(
                              (bag: any) => bag.status === "verified"
                            ).length || 0;
                            if (verifiedBags >= totalBags && totalBags > 0) {
                              return (
                                <span className="badge bg-success">Verified</span>
                              );
                            }
                            return (
                              <span className="badge bg-warning text-dark">{`${verifiedBags}/${totalBags} scanned`}</span>
                            );
                          })()
                        ) : (
                          "-"
                        )}
                      </td>
                    )}

                    <td style={tableStyles.cell}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={dispatch_items[idx]?.remarks || dispatch_items[idx]?.remark || ""}
                        onChange={(e) => handleRemarkChange(idx, e.target.value)}
                        placeholder="Enter remark"
                      />
                    </td>

                    {!isEditMode && (
                      <td
                        style={{ ...tableStyles.cell, ...tableStyles.actionCell }}
                      >
                        <div style={{ textAlign: "center" }}>
                          {item?.bags && item.bags.length > 0 ? (
                            <ActionButton
                              label="Scan"
                              icon="bi bi-qr-code-scan"
                              handleClick={() => {
                                setViewBagsForItem(item?.bags || []);
                                setCurrentItemIndex(idx);
                                setShow(true);
                              }}
                              variant="primary"
                            />
                          ) : (
                            "-"
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        <StockTransferBagsModal
          show={show}
          handleClose={() => {
            setShow(false);
            setCurrentItemIndex(null);
          }}
          bags={viewBagsForItem}
          itemIndex={currentItemIndex}
          verifiedBags={
            currentItemIndex !== null
              ? verifiedItems[currentItemIndex] || []
              : []
          }
          onBagsVerified={(
            itemIndex: number,
            verifiedBagIndices: number[],
            updatedBags: any[]
          ) => {
            setVerifiedItems((prev) => ({
              ...prev,
              [itemIndex]: verifiedBagIndices,
            }));

            // Update the bags array in dispatch_items with status
            set_dispatch_items((prevItems) => {
              const updated = prevItems.map((item: any, idx: number) => {
                if (idx === itemIndex) {
                  return {
                    ...item,
                    bags: updatedBags,
                  };
                }
                return item;
              });
              
              // Notify parent component of the update
              if (onDispatchItemsUpdate) {
                onDispatchItemsUpdate(updated);
              }
              
              return updated;
            });

            // Also add scanned entries for verified bags into scannedItems
            const dispatchId =
              dispatch_items[itemIndex]?.dispatch_item_id ||
              dispatchItems[itemIndex]?.dispatch_item_id;

            if (dispatchId) {
              setScannedItems((prev) => {
                const m = new Map(prev);
                const existing = m.get(dispatchId) || [];

                verifiedBagIndices.forEach((bagIdx) => {
                  const bag = updatedBags[bagIdx];
                  if (!bag) return;
                  const already = existing.some(
                    (s: ScannedItem) => s.inventory_item_id === bag.inventory_item_id
                  );
                  if (!already) {
                    const scannedItem: ScannedItem = {
                      dispatch_item_id: dispatchId,
                      item_id: dispatch_items[itemIndex]?.master_item_id || 
                               dispatchItems[itemIndex]?.master_item_id || "",
                      inventory_item_id: bag.inventory_item_id,
                      bag_no: bag.bag_no || "",
                      received_quantity: bag.issued_quantity || bag.total_quantity || 0,
                    };
                    existing.push(scannedItem);
                  }
                });

                if (existing.length > 0) m.set(dispatchId, existing);

                const all = Array.from(m.values()).flat();
                onItemsScanned(all);
                return m;
              });
            }
          }}
        />
      </div>
    </>
  );
};

export default StockTransferInwardTable;
